import {
  Grid,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { useEffect, useState, useRef } from "react";
import { get } from "src/utils/networkUtils";
import { generateWinnerEntries } from "src/utils/winnerEntries";

export default function WinnerUserSearch(props) {
  const { users, onUserSelect, prizeDrawId } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      setSelectedUser(users && users.length > 0 ? users[0] : null);
      isInitialMount.current = false;
    }
  }, [users]);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setLoading(true);
    const getUsers = setTimeout(() => {
      get(
        `/admin/prize-draws/entries/${prizeDrawId}/search?query=${searchQuery}`
      )
        .then((response) => {
          if (response?.users?.length) {
            const winnerEntries = generateWinnerEntries(response?.users);
            setSearchResults(winnerEntries || []);
          } else {
            setSearchResults([]);
          }
        })
        .finally(() => setLoading(false));
    }, 500);

    return () => clearTimeout(getUsers);
  }, [searchQuery, prizeDrawId]);
  useEffect(() => {
    if (!showResults) return;
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showResults]);
  const handleSelect = (user) => {
    setSelectedUser(user);
    onUserSelect(user);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
  };
  const handleDelete = () => {
    setSelectedUser(null);
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={3} sx={{ position: "relative" }} ref={searchRef}>
        <TextField
          margin="dense"
          id="kickoff"
          label="Search by username and ticket"
          type="text"
          fullWidth
          variant="standard"
          value={searchQuery}
          onFocus={() => setShowResults(true)}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowResults(true);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon={"eva:search-outline"} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <Iconify
                  icon={"eva:close-outline"}
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults([]);
                    setShowResults(false);
                  }}
                  sx={{ cursor: "pointer" }}
                />
              </InputAdornment>
            ),
          }}
          autoComplete="off"
        />
        {searchQuery && showResults && (
          <Paper
            elevation={2}
            sx={{
              position: "absolute",
              zIndex: 10,
              width: "100%",
              maxHeight: 300,
              overflowY: "auto",
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ m: 2 }} />
            ) : (
              <List dense>
                {searchResults.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No results found" />
                  </ListItem>
                ) : (
                  searchResults.map((user) => {
                    const isSelected =
                      selectedUser &&
                      selectedUser.winnerTicketNumber ===
                        user.winnerTicketNumber;
                    return (
                      <ListItemButton
                        key={user.id || user.userId}
                        onClick={() => handleSelect(user)}
                        selected={isSelected}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                          },
                        }}
                      >
                        <ListItemText
                          primary={user.username}
                          secondary={user.winnerTicketNumber}
                        />
                      </ListItemButton>
                    );
                  })
                )}
              </List>
            )}
          </Paper>
        )}
      </Grid>
      <Grid item xs={9}>
        {selectedUser && (
          <Chip
            label={selectedUser.username}
            variant="filled"
            onDelete={handleDelete}
            sx={{ margin: 0.5 }}
          />
        )}
      </Grid>
    </Grid>
  );
}
